import React, { useCallback, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { createColumnHelper } from '@tanstack/react-table'
import FiltersContext from '../../../components/Filters/FiltersContext'
import { OutPortal, createHtmlPortalNode } from 'react-reverse-portal'
import { useGetCustomerAgencyInvoiceHistory } from '../../../api/agencies/useGetCustomerAgencyInvoiceHistory'
import { useGetAgencyInvoices } from '../../../api/agencies/useGetAgencyInvoices'
import dayjs from 'dayjs'
import { ResponsiveBar } from '@nivo/bar'
import { LoadingComponent, Button, TextInput, Table } from '@demandlocal/components'
import { useParams } from 'react-router'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useGetAgencyByAgencyId } from '../../../api/agencies/useGetAgencyByAgencyId'
import { useDownloadAllOpenInvoices } from '../../../api/agencies/useDownloadAllOpenInvoices'
import { toast } from 'react-hot-toast'

type InvoiceHistoryRow = {
  number: number
  month: string
  client: string
  job_number: string
  order_number: string
  store_number: string
  invoice_amount: number
  original_invoice_amount: number
  open_amount: number
}

const columnHelper = createColumnHelper<InvoiceHistoryRow>()

const InvoiceHistory = (): JSX.Element => {
  const { agency_id } = useParams()
  const filtersPortalNode = React.useMemo(() => createHtmlPortalNode(), [])
  const [filters, setFilters] = React.useState<Partial<InvoiceHistoryRow>>({})

  useEffect(() => {
    setFilters({
      month: dayjs().subtract(1, 'months').format('YYYY-MM'),
    })
  }, [])

  const { data, isLoading: isLoadingMetrics } = useGetCustomerAgencyInvoiceHistory({
    agency_id: Number(agency_id) ?? 0,
  })

  const { data: agency } = useGetAgencyByAgencyId(agency_id)

  const chartData = useMemo(() => {
    if (isLoadingMetrics) return []
    return (
      data?.data
        .map((d) => ({
          ...d,
          invoice_amount: d.invoice_amount - d.open_amount,
        }))
        .sort((a, b) => (dayjs(a.month).isBefore(dayjs(b.month)) ? -1 : 1)) ?? []
    )
  }, [data, isLoadingMetrics])

  const openAmount = useMemo(() => {
    if (isLoadingMetrics) return 0
    return data?.data.reduce((acc, cur) => acc + cur.open_amount, 0)
  }, [data, isLoadingMetrics])

  const downloadHistoryCSV = useCallback((): void => {
    let csvContent = 'data:text/csv;charset=utf-8,'
    csvContent += `"Month","Invoice Amount","Open Amount"\n`
    data?.data.forEach(
      (d) => (csvContent += `"${d.month}","${d.invoice_amount}","${d.open_amount}"\n`),
    )
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', `open_invoices_${agency.name}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [data, agency])

  const downloadOpenInvoicesMutation = useDownloadAllOpenInvoices()

  const downloadOpenInvoicesCSV = (): void => {
    const toastId = toast.loading('Downloading...')
    downloadOpenInvoicesMutation.mutate(
      { agency_id: Number(agency_id) ?? 0 },
      {
        onSuccess(data) {
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += `"Invoice Number","Month","Client","Job Number","Order Number","Store Number","Invoice Amount","Original Invoice Amount","Open Amount"\n`
          csvContent += data?.data
            .map((d: InvoiceHistoryRow) =>
              [
                `"${d.number}"`,
                `"${d.month}"`,
                `"${d.client}"`,
                `"${d.job_number}"`,
                `"${d.order_number}"`,
                `"${d.store_number}"`,
                `"$${formatNumber(d.invoice_amount)}"`,
                `"$${formatNumber(d.original_invoice_amount)}"`,
                `"$${formatNumber(d.open_amount)}"`,
              ].join(','),
            )
            .join('\n')
          const encodedUri = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', encodedUri)
          link.setAttribute('download', `open_invoices_${agency.name}.csv`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          toast.success('Done downloading!', {
            id: toastId,
          })
        },
        onError() {
          toast.error('Failed to download', { id: toastId })
        },
      },
    )
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('number', {
        header: () => 'Invoice Number',
        cell: ({ row }) => row.original?.number,
      }),
      columnHelper.accessor('month', {
        header: () => 'Month',
        cell: ({ row }) => row.original?.month || '-',
      }),
      columnHelper.accessor('client', {
        header: () => 'Client',
        cell: ({ row }) => row.original?.client || '-',
      }),
      columnHelper.accessor('job_number', {
        header: () => 'Job Number',
        cell: ({ row }) => row.original?.job_number || '-',
      }),
      columnHelper.accessor('order_number', {
        header: () => 'Order Number',
        cell: ({ row }) => row.original?.order_number || '-',
      }),
      columnHelper.accessor('store_number', {
        header: () => 'Store Number',
        cell: ({ row }) => row.original?.store_number || '-',
      }),
      columnHelper.accessor('invoice_amount', {
        header: () => 'Invoice Amount',
        cell: ({ row }) =>
          Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
            row.original?.invoice_amount,
          ),
      }),
      columnHelper.accessor('original_invoice_amount', {
        header: () => 'Original Invoice Amount',
        cell: ({ row }) =>
          Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
            row.original?.original_invoice_amount,
          ),
      }),
      columnHelper.accessor('open_amount', {
        header: () => 'Open Amount',
        cell: ({ row }) =>
          Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
            row.original?.open_amount,
          ),
      }),
    ],
    [],
  )

  return (
    <div className={'flex flex-col gap-12 pb-12'}>
      <motion.div layout className={'flex flex-col gap-4'}>
        <div
          className={
            'font-bold text-base text-primary-xiketic flex justify-between items-center'
          }>
          <span>Invoice History</span>
          <Button onClick={() => downloadHistoryCSV()}>Export CSV</Button>
        </div>
        <motion.div className={'h-64 rounded-lg border-2 relative'}>
          {isLoadingMetrics && <LoadingComponent title={'Loading'} />}
          {!isLoadingMetrics && data?.data.length === 0 && (
            <div
              className={
                'absolute z-20 top-0 left-0 w-full h-full flex justify-center items-center'
              }>
              <span className={'text-primary-xiketic text-base'}>No data</span>
            </div>
          )}
          <ResponsiveBar
            indexBy={'month'}
            keys={['invoice_amount', 'open_amount']}
            data={chartData}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={{ scheme: 'paired' }}
            valueFormat={(value) => (value ? `$${formatNumber(value)}` : '')}
            axisTop={null}
            axisRight={null}
            padding={0.3}
            onClick={(data) => {
              setFilters({
                ...filters,
                month: data.data.month,
              })
              const input = document.querySelector(
                `input[name="month"]`,
              ) as HTMLInputElement
              if (input) input.value = dayjs(data.data.month).format('YYYY-MM')
            }}
            labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            layout={'vertical'}
            labelSkipHeight={4}
            motionConfig="stiff"
            margin={{ top: 10, right: 0, bottom: 20, left: 0 }}
            role="application"
            ariaLabel="Payment history"
          />
        </motion.div>
      </motion.div>
      <div className={'mt-4'}>
        <div className={'text-xl mb-4 flex flex-col'}>
          <div className="flex flex-row justify-between">
            <div>
              Open Amount:
              <span className={'font-bold'}>{` $${formatNumber(openAmount)}`}</span>
            </div>
            <div className={'flex gap-4'}>
              <Button animate={false} onClick={() => downloadOpenInvoicesCSV()}>
                Export Open Invoices CSV
              </Button>
              <OutPortal node={filtersPortalNode} />
            </div>
          </div>
        </div>
        <FiltersContext
          portalNode={filtersPortalNode}
          setFilters={setFilters}
          defaultValues={filters}>
          <TextInput name={'client'} placeholder={'Client name'} label={'Client name'} />
          <TextInput
            name={'month'}
            placeholder={'Month'}
            label={'Month'}
            type={'month'}
            defaultValue={filters.month}
          />
        </FiltersContext>
        <Table
          defaultSorting={[
            { id: 'month', desc: true },
            { id: 'number', desc: true },
          ]}
          columns={columns}
          filters={{ ...filters, agency_id: Number(agency_id) }}
          fetchFn={useGetAgencyInvoices}
        />
      </div>
    </div>
  )
}

export default InvoiceHistory
